import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { PacienteService } from '../services/pacienteService';
import { ConsultasService, RecentConsulta } from '../services/consultasService';

export interface DashboardStats {
  totalPacientes: number;
  totalConsultas: number;
  consultasThisMonth: number;
  consultasToday: number;
  upcomingConsultas: number;
}

interface UseDashboardReturn {
  stats: DashboardStats;
  recentConsultas: RecentConsulta[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const useDashboard = (): UseDashboardReturn => {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalPacientes: 0,
    consultasToday: 0,
    consultasThisMonth: 0,
    totalConsultas: 0,
    upcomingConsultas: 0
  });
  const [recentConsultas, setRecentConsultas] = useState<RecentConsulta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getStats = useCallback(async (): Promise<void> => {
    if (!currentOrganization) return;

    setLoading(true);
    setError(null);
    try {
      const [
        totalPacientesResult,
        totalConsultasResult,
        consultasTodayResult,
        consultasThisMonthResult
      ] = await Promise.all([
        PacienteService.getPacienteCount(currentOrganization.id),
        ConsultasService.getConsultasCount(currentOrganization.id),
        ConsultasService.getConsultasTodayCount(currentOrganization.id),
        ConsultasService.getConsultasThisMonthCount(currentOrganization.id)
      ]);

      if (!totalPacientesResult.success) throw new Error(totalPacientesResult.message);
      if (!totalConsultasResult.success) throw new Error(totalConsultasResult.message);
      if (!consultasTodayResult.success) throw new Error(consultasTodayResult.message);
      if (!consultasThisMonthResult.success) throw new Error(consultasThisMonthResult.message);

      setStats({
        totalPacientes: totalPacientesResult.count || 0,
        totalConsultas: totalConsultasResult.count || 0,
        consultasToday: consultasTodayResult.count || 0,
        consultasThisMonth: consultasThisMonthResult.count || 0,
        upcomingConsultas: 0
      });
    } catch (error) {
      if ((error as any)?.status === 401) navigate('/login');
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [navigate, currentOrganization]);

  const getRecentConsultas = useCallback(async (): Promise<void> => {
    if (!currentOrganization) return;

    try {
      const response = await ConsultasService.getRecentConsultas(currentOrganization.id, 5);

      if (response.success && response.data) {
        setRecentConsultas(response.data);
      } else {
        setError(response.message);
        if (response.error?.message.includes('401')) {
          navigate('/login');
        }
      }
    } catch (error) {
      setError((error as Error).message);
    }
  }, [navigate, currentOrganization]);

  useEffect(() => {
    getStats();
    getRecentConsultas();
  }, [getStats, getRecentConsultas]);

  return {
    stats,
    recentConsultas,
    loading,
    error,
    refresh: () => {
      getStats();
      getRecentConsultas();
    }
  };
};

export default useDashboard;
